import React, { useCallback, useEffect, useMemo, useState } from "react";
import api from "../lib/api";
import { questionIsVisible } from "../lib/questionnaireConditions";
import ArtistCategoryMultiSelect from "./ArtistCategoryMultiSelect";

/**
 * Guided step-by-step onboarding wizard driven entirely by the
 * /questionnaire/universal + /questionnaire/category/{slug} metadata.
 *
 * Saves after every section so the artist never loses progress.
 * Emits `onComplete(answers)` when the last section is submitted.
 */
export default function QuestionnaireWizard({ category, onComplete, initialAnswers = null, startSection = null }) {
  const [layer1, setLayer1] = useState([]);
  const [layer2, setLayer2] = useState([]);
  const [answers, setAnswers] = useState(initialAnswers || {});
  const [stepIdx, setStepIdx] = useState(0);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  // Iter 73 — Sidebar dropdown items must be able to jump to ANY section
  // repeatedly (not just once), so we drive deep-linking from `startSection`
  // directly. The previous `deepLinkApplied` boolean guard swallowed every
  // click after the first — this bug is what the artist reported.
  const wizardRef = React.useRef(null);

  const selectedCategories = useMemo(() => (
    Array.isArray(answers.category)
      ? answers.category
      : answers.category
        ? [answers.category]
        : category
          ? [category]
          : []
  ), [answers.category, category]);
  const liveCategories = useMemo(() => (
    selectedCategories
      .map((item) => item === "Other" ? (answers.category_other || "Other") : item)
      .filter(Boolean)
  ), [answers.category_other, selectedCategories]);
  const categoryKey = liveCategories.join("|");

  // Fetch Layer 1 + existing answers on mount
  useEffect(() => {
    let cancelled = false;
    Promise.all([
      api.get("/questionnaire/universal"),
      initialAnswers ? Promise.resolve({ data: initialAnswers }) : api.get("/questionnaire/answers/mine").catch(() => ({ data: {} })),
    ]).then(([u, mine]) => {
      if (cancelled) return;
      setLayer1(u.data || []);
      const mergedAnswers = { ...(mine.data || {}), ...(initialAnswers || {}) };
      const categoryQuestion = (u.data || []).find((question) => question.id === "category");
      if (!mergedAnswers.category && category) {
        if (categoryQuestion?.options?.includes(category)) mergedAnswers.category = category;
        else {
          mergedAnswers.category = "Other";
          mergedAnswers.category_other = category;
        }
      }
      if (mergedAnswers.category && !Array.isArray(mergedAnswers.category)) {
        mergedAnswers.category = [mergedAnswers.category];
      } else if (!mergedAnswers.category && category) {
        mergedAnswers.category = [category];
      }
      setAnswers(mergedAnswers);
      setLoading(false);
    }).catch((e) => {
      if (!cancelled) { setErr(e); setLoading(false); }
    });
    return () => { cancelled = true; };
  }, [initialAnswers, category]);

  // Re-fetch Layer 2 whenever the selected categories change.
  useEffect(() => {
    if (!liveCategories.length) { setLayer2([]); return; }
    let cancelled = false;
    Promise.all(liveCategories.map((item) => (
      api.get(`/questionnaire/category/${encodeURIComponent(item)}`).then((r) => r.data || [])
    )))
      .then((responses) => {
        if (cancelled) return;
        const seen = new Set();
        setLayer2(responses.flat().filter((question) => {
          const key = question.id || question.key;
          if (!key || seen.has(key)) return false;
          seen.add(key);
          return true;
        }));
      })
      .catch(() => { if (!cancelled) setLayer2([]); });
    return () => { cancelled = true; };
  }, [liveCategories, categoryKey]);

  // Skip-logic helper: hide a question if `show_if` is set and doesn't match.
  // Supports: { question_id: value }, { question_id: [v1, v2] }, or the shortcut
  // { category: "DJ / Music Producer" } for category-specific gating.
  const shouldShow = useCallback((q) => {
    return questionIsVisible(q, answers, liveCategories);
  }, [liveCategories, answers]);

  // Group Layer 1 questions by section, keep original order per section
  const sections = useMemo(() => {
    const byS = new Map();
    for (const q of layer1) {
      if (!shouldShow(q)) continue;
      if (q.id === "languages") continue;
      const s = q.section || "Other";
      if (!byS.has(s)) byS.set(s, []);
      byS.get(s).push(q);
    }
    const arr = Array.from(byS.entries()).map(([section, qs]) => ({ section, questions: qs }));
    const visibleLayer2 = layer2.filter((q) => shouldShow(q) && q.id !== "languages");
    if (visibleLayer2.length > 0) {
      arr.push({ section: liveCategories.length ? `${liveCategories.join(" & ")} specifics` : "Category specifics", questions: visibleLayer2 });
    }
    return arr;
  }, [layer1, layer2, liveCategories, shouldShow]);

  const totalSteps = sections.length;
  const current = sections[stepIdx];
  const isLast = stepIdx === totalSteps - 1;
  const startSectionIndex = startSection
    ? sections.findIndex((section) => (section.section || "").toLowerCase() === startSection.toLowerCase())
    : -1;

  // Deep-link when navigation selects a section, not whenever its answers change.
  useEffect(() => {
    if (!startSection || loading || startSectionIndex < 0) return;
    setStepIdx(startSectionIndex);
    requestAnimationFrame(() => {
      wizardRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    });
  }, [startSection, loading, startSectionIndex]);

  const setAns = (id, val) => setAnswers((current) => {
    const next = { ...current, [id]: val };
    const nextCategory = Array.isArray(next.category) ? next.category : [next.category || category].filter(Boolean);
    [...layer1, ...layer2].forEach((question) => {
      const questionId = question.id || question.key;
      if (questionId !== id && question.show_if && !questionIsVisible(question, next, nextCategory)) {
        delete next[questionId];
      }
    });
    return next;
  });

  // Validate required fields in the current section
  const missing = useMemo(() => {
    if (!current) return [];
    return current.questions.filter((q) => {
      const empty = answers[q.id] === undefined
        || answers[q.id] === ""
        || answers[q.id] === null
        || (Array.isArray(answers[q.id]) && answers[q.id].length === 0);
      if (!q.required || empty) {
        return q.required && empty;
      }
      if (q.type === "url") {
        if (q.id === "instagram_account" && !/^https?:\/\/(?:www\.)?instagram\.com\/[A-Za-z0-9._%-]+\/?(?:\?.*)?$/i.test(String(answers[q.id]).trim())) {
          return true;
        }
        try {
          const parsed = new URL(String(answers[q.id]).trim());
          return !["http:", "https:"].includes(parsed.protocol);
        } catch {
          return true;
        }
      }
      if (q.id === "instagram_followers") {
        const value = Number(answers[q.id]);
        return !Number.isInteger(value) || value < 0;
      }
      return false;
    });
  }, [current, answers]);

  const saveSection = async () => {
    if (!current) return true;
    const payload = {};
    current.questions.forEach((q) => {
      if (answers[q.id] !== undefined) payload[q.id] = answers[q.id];
    });
    if (current.questions.some((q) => q.id === "category") && answers.category_other !== undefined) {
      payload.category_other = answers.category_other;
    }
    if (current.questions.some((q) => q.id === "languages") && answers.languages_other !== undefined) {
      payload.languages_other = answers.languages_other;
    }
    current.questions.forEach((q) => {
      if (q.other_answer_id && answers[q.other_answer_id] !== undefined) {
        payload[q.other_answer_id] = answers[q.other_answer_id];
      }
    });
    try {
      setSaving(true);
      await api.post("/questionnaire/answers", { answers: payload });
      return true;
    } catch (e) {
      setErr(e);
      return false;
    } finally { setSaving(false); }
  };

  const next = async () => {
    if (missing.length > 0) return;
    const ok = await saveSection();
    if (!ok) return;
    if (isLast) {
      onComplete && onComplete(answers);
    } else {
      setStepIdx((i) => Math.min(totalSteps - 1, i + 1));
    }
  };

  if (loading) return <div className="card card-pad text-center">Loading your questionnaire…</div>;
  if (err) return <div className="card card-pad text-center text-muted">Couldn't load the questionnaire. Please refresh.</div>;
  if (!current) return null;

  return (
    <div className="q-wizard" data-testid="q-wizard" ref={wizardRef}>
      <nav className="q-progress" aria-label="Questionnaire sections">
        {sections.map((s, i) => {
          // Status comes from the artist's actual answers, not from position,
          // so a skipped section still reads as missing.
          const answeredCount = s.questions.filter((q) => {
            const v = answers[q.id];
            return v !== undefined && v !== null && v !== "" && !(Array.isArray(v) && v.length === 0);
          }).length;
          const state = answeredCount === 0 ? "empty" : answeredCount >= s.questions.length ? "done" : "partial";
          const stateLabel = state === "done" ? "completed" : state === "empty" ? "not started" : `${answeredCount} of ${s.questions.length} answered`;
          return (
            <button
              type="button"
              key={s.section}
              className={`q-progress-step ${state} ${i === stepIdx ? "current" : ""}`}
              onClick={() => setStepIdx(i)}
              data-testid={`q-step-${i}`}
              title={`${s.section} · ${stateLabel}`}
              aria-current={i === stepIdx ? "step" : undefined}
              aria-label={`Step ${i + 1} of ${totalSteps}: ${s.section}, ${stateLabel}${i === stepIdx ? ", current" : ""}`}
            >
              <div className="q-progress-dot">{state === "done" ? "✓" : state === "empty" ? "?" : i + 1}</div>
              <div className="q-progress-label">{s.section}</div>
            </button>
          );
        })}
      </nav>

      <div className="card card-pad q-body" data-testid="q-body">
        <h2 className="font-serif fs-22 fw-700 mb-4">{current.section}</h2>
        <p className="text-muted fs-13 mb-20">Step {stepIdx + 1} of {totalSteps} · your answers save as you go</p>
        <div className="q-fields">
            {current.questions.map((q) => (
              <FieldRenderer
                key={q.id}
                q={q}
                value={answers[q.id]}
                answers={answers}
                onAnswer={setAns}
                onChange={(v) => setAns(q.id, v)}
              />
          ))}
        </div>
        {missing.length > 0 && (
          <div className="q-missing" data-testid="q-missing">
            Please fill in: <strong>{missing.map((m) => m.label).join(", ")}</strong>
          </div>
        )}
        <div className="flex justify-between mt-24">
          <button
            type="button"
            className="btn btn-ghost"
            onClick={() => setStepIdx((i) => Math.max(0, i - 1))}
            disabled={stepIdx === 0 || saving}
            data-testid="q-back"
          >← Back</button>
          <button
            type="button"
            className="btn btn-gold"
            onClick={next}
            disabled={missing.length > 0 || saving}
            data-testid="q-next"
          >
            {saving ? "Saving…" : isLast ? "Finish Onboarding →" : "Save & Continue →"}
          </button>
        </div>
      </div>
    </div>
  );
}

function FieldRenderer({ q, value, answers = {}, onAnswer, onChange }) {
  const label = (
    <>
      <div className="field-label">
        {q.label}{q.required && <span className="text-gold" aria-label="required"> *</span>}
      </div>
      {q.description && <div className="text-muted fs-11 mb-8" data-testid={`q-help-${q.id}`}>{q.description}</div>}
    </>
  );
  if (q.id === "category") {
    return (
      <div className="field">
        {label}
        <ArtistCategoryMultiSelect
          categories={Array.isArray(value) ? value : value ? [value] : []}
          options={q.options || []}
          onChange={onChange}
          testId="q-category"
        />
        {Array.isArray(value) && value.includes("Other") && (
          <input
            className="field-input mt-8"
            value={answers.category_other || ""}
            onChange={(e) => onAnswer?.("category_other", e.target.value)}
            placeholder="Enter your artist category"
            data-testid="q-category-other"
          />
        )}
      </div>
    );
  }
  switch (q.type) {
    case "textarea":
      return (
        <div className="field">
          {label}
          <textarea className="field-input" rows={4} value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={`q-${q.id}`} />
        </div>
      );
    case "select":
      return (
        <div className="field">
          {label}
          <select className="field-input" value={value || ""} onChange={(e) => {
            onChange(e.target.value);
            if (q.id === "category" && e.target.value !== "Other") onAnswer?.("category_other", "");
          }} data-testid={`q-${q.id}`}>
            <option value="">Select…</option>
            {(q.options || []).map((o) => <option key={o} value={o}>{q.option_labels?.[o] || o}</option>)}
          </select>
          {q.id === "category" && value === "Other" && (
            <input
              className="field-input mt-8"
              value={answers.category_other || ""}
              onChange={(e) => onAnswer?.("category_other", e.target.value)}
              placeholder="Enter your artist category"
              data-testid="q-category-other"
            />
          )}
          {q.id === "languages" && Array.isArray(value) && value.includes("Other") && (
            <input
              className="field-input mt-8"
              value={answers.languages_other || ""}
              onChange={(e) => onAnswer?.("languages_other", e.target.value)}
              placeholder="Enter another language"
              data-testid="q-languages-other"
            />
          )}
        </div>
      );
    case "multiselect":
      return (
        <div className="field">
          {label}
          <div className="q-multi">
            {(q.options || []).map((o) => {
              const arr = Array.isArray(value) ? value : [];
              const on = arr.includes(o);
              return (
                <button
                  type="button"
                  key={o}
                  className={`mult-chip ${on ? "active" : ""}`}
                  onClick={() => {
                    let updated = on ? arr.filter((x) => x !== o) : [...arr, o];
                    const exclusiveOption = q.id === "artist_brings" ? "Nothing" : "No Preference";
                    if (["travel_modes", "speaker_brand", "mixer_brand", "artist_brings"].includes(q.id) && !on) {
                      updated = o === exclusiveOption
                        ? [o]
                        : [...arr.filter((x) => x !== exclusiveOption), o];
                    }
                    onChange(updated);
                    if (q.id === "languages" && o === "Other" && on) onAnswer?.("languages_other", "");
                    if (q.other_answer_id && o === "Other" && on) onAnswer?.(q.other_answer_id, "");
                  }}
                  data-testid={`q-${q.id}-${o}`}
                >
                  {o}
                </button>
              );
            })}
          </div>
          {q.other_answer_id && Array.isArray(value) && value.includes("Other") && (
            <input
              className="field-input mt-8"
              value={answers[q.other_answer_id] || ""}
              onChange={(e) => onAnswer?.(q.other_answer_id, e.target.value)}
              placeholder={q.other_placeholder || "Please specify"}
              data-testid={`q-${q.id}-other`}
            />
          )}
        </div>
      );
    case "boolean":
    case "toggle":
      return (
        <div className="field q-bool">
          {label}
          <div className="q-multi">
            <button type="button" className={`mult-chip ${value === true ? "active" : ""}`} onClick={() => onChange(true)} data-testid={`q-${q.id}-yes`}>Yes</button>
            <button type="button" className={`mult-chip ${value === false ? "active" : ""}`} onClick={() => onChange(false)} data-testid={`q-${q.id}-no`}>No</button>
          </div>
        </div>
      );
    case "number":
      return (
        <div className="field">
          {label}
          <input type="number" min="0" step={q.step || 1} className="field-input" value={value ?? ""} onChange={(e) => onChange(e.target.value === "" ? "" : Number(e.target.value))} data-testid={`q-${q.id}`} />
        </div>
      );
    case "price":
      return (
        <div className="field">
          {label}
          <div className={`q-price ${q.hide_currency_symbol ? "q-price-no-prefix" : ""}`}>
            {!q.hide_currency_symbol && <span className="q-price-prefix">₹</span>}
            <input type="number" min="0" step="100" className="field-input" value={value ?? ""} onChange={(e) => onChange(e.target.value === "" ? "" : Number(e.target.value))} data-testid={`q-${q.id}`} />
          </div>
        </div>
      );
    case "time":
      return (
        <div className="field">
          {label}
          <input type="time" className="field-input" value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={`q-${q.id}`} />
        </div>
      );
    case "date":
      return (
        <div className="field">
          {label}
          <input type="date" className="field-input" value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={`q-${q.id}`} />
        </div>
      );
    case "file":
    case "photo":
    case "photos":
    case "video":
    case "videos":
    case "media":
    case "attachment":
    case "image":
      // Iter 55 — Photo / video / media questions must NEVER open a separate
      // upload dialog. Every artist upload flows through the Media & Photos
      // module so all assets live in one gallery + CDN pipeline.
      return (
        <div className="field">
          {label}
          <div className="q-file-hint" data-testid={`q-media-hint-${q.id}`}>
            <span style={{ fontSize: 18, marginRight: 8 }}></span>
            <span>
              Upload happens on the <b>Media &amp; Photos</b> tab of your dashboard, so all your assets live in one gallery.
              Skip this here — we'll take you there right after you finish the questionnaire.
            </span>
          </div>
        </div>
      );
    case "info":
      return (
        <div className="field q-info">
          {label}
        </div>
      );
    case "tel":
    case "url":
    case "text":
    default:
      return (
        <div className="field">
          {label}
          <input type={q.type === "tel" ? "tel" : q.type === "url" ? "url" : "text"} className="field-input" value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={`q-${q.id}`} />
        </div>
      );
  }
}
